import * as React from 'react'
import {cva, type VariantProps} from 'class-variance-authority'
import {cn} from '@/lib/utils'

const bodyCopyVariants = cva('', {
    variants: {
        size: {
            small: 'body-sm',
            medium: 'body-md',
            large: 'body-lg'
        }
    },
    defaultVariants: {
        size: 'medium'
    }
})

type Size = 'small' | 'medium' | 'large'

interface BodyCopyProps
    extends React.HTMLAttributes<HTMLParagraphElement>,
        VariantProps<typeof bodyCopyVariants> {
    size?: Size
    'data-testid': string | undefined
}

const isValidSize = (size: string): size is Size => {
    return ['small', 'medium', 'large'].includes(size)
}

const BodyCopy = React.forwardRef<HTMLParagraphElement, BodyCopyProps>(
    ({size = 'medium', className, children, 'data-testid': dataTestId, ...props}, ref) => {
        // Default to 'medium' if `size` is not valid
        const sizeValue = isValidSize(size) ? size : 'medium'

        return (
            <p
                className={cn(bodyCopyVariants({size: sizeValue}), className)}
                ref={ref}
                data-testid={dataTestId || 'BodyCopy'}
                {...props}
            >
                {children}
            </p>
        )
    }
)

BodyCopy.displayName = 'BodyCopy'

export {BodyCopy}
